import React, { useState, useEffect } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Box, Typography, Button, IconButton, Stack, Paper, Alert, CircularProgress
} from '@mui/material';

const validTypes = [
    "image/jpg", "image/jpeg", "image/png", "image/gif", "image/webp",
    "application/pdf", "message/rfc822", "application/octet-stream"
];

const ReceiptDialogEditDocument = ({ isOpen, onClose, receipt, onSave }) => {
    const [selectedFile, setSelectedFile] = useState(null);
    const [previewUrl, setPreviewUrl] = useState(null);
    const [isDragging, setIsDragging] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [error, setError] = useState('');

    // Reset state on open/close
    useEffect(() => {
        if (!isOpen) {
            setSelectedFile(null);
            setPreviewUrl(null);
            setError('');
            setIsUploading(false);
        }
    }, [isOpen]);

    const handleFileChange = (file) => {
        setError('');
        if (!file) return;

        if (!validTypes.includes(file.type)) {
            setError('Invalid file type. Please upload an image, PDF, or email file.');
            return;
        }

        setSelectedFile(file);

        // Preview logic for images
        if (file.type.startsWith('image/')) {
            const reader = new FileReader();
            reader.onloadend = () => setPreviewUrl(reader.result);
            reader.readAsDataURL(file);
        } else {
            setPreviewUrl(null);
        }
    };

    const onDrop = (e) => {
        e.preventDefault();
        setIsDragging(false);
        if (isUploading) return;
        handleFileChange(e.dataTransfer.files[0]);
    };

    const handleSave = () => {
        if (!selectedFile) return;

        // Send the data to the parent
        onSave(receipt.id, {
            new_file_binary: selectedFile,
            file_name: selectedFile.name,
            file_type: selectedFile.type,
            file_size: selectedFile.size
        });

        // Close immediately
        onClose();
    };

    const getFileIcon = (type) => {
        if (type === 'application/pdf') return 'icon-file-pdf';
        if (type === 'message/rfc822') return 'icon-envelope';
        return 'icon-file-text';
    };

    return (
        <Dialog
            open={isOpen}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{ sx: { borderRadius: '24px', overflow: 'hidden' } }}
        >
            {/* Gradient Header */}
            <DialogTitle sx={{
                background: 'linear-gradient(135deg, #151269 0%, #352fcc 100%)',
                color: 'white', fontWeight: 800, display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 2
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <i className="icon-file-plus" style={{ fontSize: '20px' }} />
                    <Typography variant="h6" sx={{ fontWeight: 800 }}>Replace Document</Typography>
                </Box>
                <IconButton onClick={onClose} sx={{ color: 'white', opacity: 0.8 }} disabled={isUploading}>
                    <i className="icon-cross" />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ p: 4, bgcolor: '#fcfcfc' }}>
                <Stack spacing={3}>
                    {error && <Alert severity="error" sx={{ borderRadius: '12px' }}>{error}</Alert>}

                    {/* Show Current State vs New State */}
                    {!selectedFile && !isUploading && (
                        <Box sx={{ textAlign: 'center' }}>
                            <Typography variant="caption" sx={{ fontWeight: 900, color: '#151269', opacity: 0.4, mb: 1.5, display: 'block', letterSpacing: '1px' }}>
                                CURRENTLY ATTACHED
                            </Typography>
                            <Paper elevation={0} sx={{
                                borderRadius: '16px', overflow: 'hidden', height: '70px', width: '110px',
                                margin: '0 auto', border: '2px solid #eee', bgcolor: '#f8f9fa',
                                display: 'flex', alignItems: 'center', justifyContent: 'center'
                            }}>
                                {receipt?.signedUrl ? (
                                    <img
                                        src={receipt.signedUrl}
                                        alt="Current"
                                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                        // 🎯 FALLBACK: If the URL is expired, the browser triggers onError
                                        onError={(e) => {
                                            e.target.style.display = 'none'; // Hide the broken image
                                            e.target.parentNode.innerHTML = '<i class="icon-file-text" style="color: #ccc; font-size: 24px;"></i>';
                                        }}
                                    />
                                ) : (
                                    <i className="icon-file-text" style={{ color: '#ccc', fontSize: '24px' }} />
                                )}
                            </Paper>
                            {/* Optional: Visual hint that the link is old */}
                            {receipt?.urlTimestamp && Date.now() > (receipt.urlTimestamp + 3600000) && (
                                <Typography variant="caption" sx={{ color: '#ff9800', fontSize: '10px', mt: 0.5, display: 'block' }}>
                                    Preview link expired
                                </Typography>
                            )}
                        </Box>
                    )}

                    {/* Main Interaction Zone */}
                    <Box
                        onDragOver={(e) => { e.preventDefault(); !isUploading && setIsDragging(true); }}
                        onDragLeave={() => setIsDragging(false)}
                        onDrop={onDrop}
                        onClick={() => !isUploading && document.getElementById('replaceFileInput').click()}
                        sx={{
                            width: '100%',
                            height: '280px',
                            borderRadius: '28px',
                            border: `3px dashed ${isDragging ? '#352fcc' : '#e0e0e0'}`,
                            bgcolor: isDragging ? 'rgba(53, 47, 204, 0.04)' : '#f8f9fa',
                            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                            cursor: isUploading ? 'default' : 'pointer',
                            position: 'relative',
                            '&:hover': {
                                borderColor: isUploading ? '#e0e0e0' : '#151269',
                                bgcolor: isUploading ? '#f8f9fa' : '#f4f5f7',
                                transform: isUploading ? 'none' : 'scale(1.01)'
                            }
                        }}
                    >
                        <input
                            id="replaceFileInput"
                            type="file"
                            hidden
                            onChange={(e) => handleFileChange(e.target.files[0])}
                            accept={validTypes.join(',')}
                        />

                        {isUploading ? (
                            <Stack alignItems="center" spacing={2.5}>
                                <CircularProgress size={50} thickness={4.5} sx={{ color: '#151269' }} />
                                <Box sx={{ textAlign: 'center' }}>
                                    <Typography variant="body1" sx={{ fontWeight: 800, color: '#151269' }}>Uploading to Cloud...</Typography>
                                    <Typography variant="caption" color="textSecondary">Please do not close this window</Typography>
                                </Box>
                            </Stack>
                        ) : previewUrl ? (
                            <Box sx={{ width: '100%', height: '100%', p: 2, position: 'relative' }}>
                                <img src={previewUrl} style={{ width: '100%', height: '100%', objectFit: 'contain', borderRadius: '16px' }} />
                                <Box sx={{
                                    position: 'absolute', bottom: 15, left: 15, right: 15,
                                    bgcolor: 'rgba(21, 18, 105, 0.95)', color: 'white',
                                    py: 1, px: 2, borderRadius: '12px', textAlign: 'center',
                                    backdropFilter: 'blur(4px)'
                                }}>
                                    <Typography variant="caption" sx={{ fontWeight: 700, display: 'block' }}>{selectedFile?.name}</Typography>
                                </Box>
                            </Box>
                        ) : selectedFile ? (
                            <Stack alignItems="center" spacing={2}>
                                <Box sx={{
                                    width: 80, height: 80, borderRadius: '20px', bgcolor: 'white',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    boxShadow: '0 8px 20px rgba(0,0,0,0.06)'
                                }}>
                                    <i className={getFileIcon(selectedFile.type)} style={{ fontSize: '40px', color: '#151269' }} />
                                </Box>
                                <Box sx={{ textAlign: 'center' }}>
                                    <Typography variant="body2" sx={{ fontWeight: 800 }}>{selectedFile.name}</Typography>
                                    <Typography variant="caption" sx={{ bgcolor: '#15126910', color: '#151269', px: 1, py: 0.3, borderRadius: '4px', fontWeight: 700 }}>
                                        {(selectedFile.size / 1024).toFixed(1)} KB
                                    </Typography>
                                </Box>
                                <Button size="small" sx={{ color: '#352fcc', fontWeight: 800, textTransform: 'none' }}>Change Selection</Button>
                            </Stack>
                        ) : (
                            <Stack alignItems="center" spacing={2.5}>
                                <Box sx={{
                                    width: 64, height: 64, borderRadius: '50%', bgcolor: 'white',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    boxShadow: '0 6px 16px rgba(0,0,0,0.08)'
                                }}>
                                    <i className="icon-upload-cloud" style={{ fontSize: '32px', color: '#151269' }} />
                                </Box>
                                <Box sx={{ textAlign: 'center' }}>
                                    <Typography variant="h6" sx={{ fontWeight: 800, color: '#151269', mb: 0.5 }}>
                                        Select New Document
                                    </Typography>
                                    <Typography variant="body2" color="textSecondary" sx={{ px: 4 }}>
                                        Drag & drop or click to browse for your replacement file
                                    </Typography>
                                </Box>
                            </Stack>
                        )}
                    </Box>
                </Stack>
            </DialogContent>

            <DialogActions sx={{ p: 3, bgcolor: '#f8f9fa', borderTop: '1px solid #eee' }}>
                <Button
                    onClick={onClose}
                    disabled={isUploading}
                    sx={{ color: '#666', fontWeight: 700, px: 3 }}
                >
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    disabled={!selectedFile || isUploading}
                    onClick={handleSave}
                    sx={{
                        bgcolor: '#151269',
                        borderRadius: '14px',
                        px: 5,
                        py: 1.2,
                        fontWeight: 800,
                        boxShadow: '0 4px 14px rgba(21, 18, 105, 0.25)',
                        '&:hover': { bgcolor: '#251eb2', boxShadow: '0 6px 20px rgba(21, 18, 105, 0.3)' },
                        '&.Mui-disabled': { bgcolor: '#e0e0e0', color: '#aaa' }
                    }}
                >
                    {isUploading ? 'Uploading...' : 'Confirm Replacement'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ReceiptDialogEditDocument;